import React, { useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Spinner,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FaInfoCircle } from 'react-icons/fa';
import { BsFillInfoCircleFill, FiPlusCircle, FiXCircle } from 'react-icons/all';
import { Link as ChakraLink } from '@chakra-ui/layout/dist/types/link';
import { useHistory } from 'react-router';
import { Heading, Input, Table } from '~/components';
import { AttachCertificateDetailsView } from '~/types/zeroCarbon';
import { FileUploader } from '~/components/FileInput';
import { validateNotificationEmail } from '~/utils/emailRegex';
import { useLinkedContracts } from '~/hooks/useZeroCarbon/useGroupZeroCarbon';
import { putAttachCertificate } from '~/services/GroupZeroCarbon';
import { queryClient } from '~/utils/react-query';
import { ROUTER_PATHS, STORAGE } from '~/constants';
import { replaceParameters } from '~/utils/replaceParamaters';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';

const AttachCertificateFormSchema = yup.object().shape({
  codigo_certificado: yup
    .string()
    .required('Código do certificado obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  link_certificado: yup
    .string()
    .url('Link inválido')
    .required('Link do certificado obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  anexo_certificadora_nome: yup
    .string()
    .required('Anexo obrigatório')
    .max(100, 'Máximo de 100 caracteres'),
  anexo_validadora_nome: yup
    .string()
    .required('Anexo obrigatório')
    .max(100, 'Máximo de 100 caracteres'),
  clientes: yup.array().of(
    yup.object().shape({
      nome_cliente: yup.string().required('Nome do cliente obrigatório'),
      email_cliente: yup
        .string()
        .email('E-mail inválido')
        .required('E-mail obrigatório'),
    }),
  ),
});

interface Certificado {
  preview: AttachCertificateDetailsView;
  codigo_certificado: string;
  link_certificado: string;
  anexo_certificadora_file: File;
  anexo_certificadora_nome: string;
  anexo_validadora_file: File;
  anexo_validadora_nome: string;
  clientes: Cliente[];
}

interface Cliente {
  nome_cliente: string;
  email_cliente: string;
}

export const AttachCertificateDialog: React.FC<{
  id_projeto_co2: string;
  possui_carta: boolean;
}> = ({ id_projeto_co2, possui_carta }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenDialog,
    onOpen: onOpenDialog,
    onClose: onCloseDialog,
  } = useDisclosure();
  const toast = useToast();
  const history = useHistory();
  const [isFetching, setIsFetching] = useState(false);
  const { formState, getValues, setValue, setError, clearErrors, control } =
    useForm({
      resolver: yupResolver(AttachCertificateFormSchema),
    });
  const { errors } = formState;
  const certificate = {
    preview: {
      id: 0,
      projeto: '',
      contratos: [],
      total_compensado_co2_t: '',
      total_consumido_co2_kg: '',
      saldo_co2_kg: '',
      formattedTotalCompensadoCo2t: '',
      formattedTotalConsumidoCo2Kg: '',
      formattedSaldoCo2Kg: '',
      consumo_disponivel_co2: '',
    } as AttachCertificateDetailsView,
    codigo_certificado: '',
    link_certificado: '',
    anexo_certificadora_file: {} as File,
    anexo_certificadora_nome: '',
    anexo_validadora_file: {} as File,
    anexo_validadora_nome: '',
    clientes: [{ nome_cliente: '', email_cliente: '' }] as Cliente[],
  } as Certificado;
  const [certificado, setCertificado] = useState<Certificado>(certificate);
  const {
    data: contratos_relacionados,
    isLoading: isLoadingContratos,
    refetch: refetchContratosRelacionados,
  } = useLinkedContracts(id_projeto_co2, {
    onSuccess: data => {
      setCertificado({
        ...certificado,
        preview: data,
      });
    },
  });

  const handleClose = () => {
    clearErrors();
    onClose();
    setCertificado(certificate);
  };

  const handleOpen = () => {
    onOpen();
    refetchContratosRelacionados();
  };

  const possuiErros = () => {
    let temErros = false;
    try {
      AttachCertificateFormSchema.validateSync(certificado, {
        abortEarly: false,
      });
      clearErrors();
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        err.inner.forEach(e => {
          setError(e.path!, { message: e.message });
        });
        temErros = true;
      }
    }
    if (certificado.anexo_validadora_file.size > 5 * 1024 * 1024) {
      setError('anexo_validadora_nome', {
        type: 'maxSize',
        message: 'O arquivo deve ter no máximo 5MB',
      });
      temErros = true;
    }
    if (certificado.anexo_certificadora_file.size > 5 * 1024 * 1024) {
      setError('anexo_certificadora_nome', {
        type: 'maxSize',
        message: 'O arquivo deve ter no máximo 5MB',
      });
      temErros = true;
    }
    certificado.clientes.forEach((cliente, index) => {
      if (!cliente.nome_cliente.trim()) {
        setError(`nome_cliente-${index}`, {
          type: 'required',
          message: 'Campo obrigatório',
        });
        temErros = true;
      }
      if (cliente.nome_cliente.length > 50) {
        setError(`nome_cliente-${index}`, {
          type: 'maxSize',
          message: 'Máximo de 50 caracteres',
        });
        temErros = true;
      }
      if (!cliente.email_cliente.trim()) {
        setError(`email_cliente-${index}`, {
          type: 'required',
          message: 'Campo obrigatório',
        });
        temErros = true;
      }
    });
    return temErros;
  };

  const salvar = () => {
    if (possuiErros()) {
      return;
    }

    const formData = new FormData();
    formData.append('codigo_certificado', certificado.codigo_certificado);
    formData.append('link_certificado', certificado.link_certificado);
    formData.append(
      'anexo_certificado_certificadora',
      certificado.anexo_certificadora_file,
    );
    formData.append(
      'anexo_certificado_validadora',
      certificado.anexo_validadora_file,
    );
    const emails_clientes = certificado.clientes.map(it => {
      return {
        nome: it.nome_cliente,
        email: it.email_cliente,
      };
    });
    formData.append('emails_clientes', JSON.stringify(emails_clientes));

    setIsFetching(true);
    putAttachCertificate(id_projeto_co2, formData)
      .then(() => {
        handleClose();
        queryClient.invalidateQueries('group_certificates_zero_carbon');
        queryClient.invalidateQueries('contract');
        toast({
          title: 'Certificado anexado com sucesso!',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        clearEmailStorage();
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data.detail,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetching(false);
      });
  };
  const visualizarEmail = () => {
    if (possuiErros()) {
      return;
    }

    let clientesStr = '';

    clientesStr = certificado.clientes
      .map(cliente => cliente.nome_cliente)
      .join(', ');

    localStorage.setItem(`${STORAGE}:clientes`, clientesStr);
    localStorage.setItem(
      `${STORAGE}:codigo_cert`,
      `<a href="${certificado.link_certificado}" rel="noopener noreferrer" target="_blank">${certificado.codigo_certificado}</a>`,
    );
    localStorage.setItem(`${STORAGE}:link_cert`, certificado.link_certificado);
    localStorage.setItem(
      `${STORAGE}:total_t`,
      certificado.preview?.formattedTotalCompensadoCo2t,
    );
    localStorage.setItem(
      `${STORAGE}:nome_projeto`,
      certificado.preview.projeto,
    );
    window.open(
      replaceParameters(ROUTER_PATHS.VIEW_LETTER, String(id_projeto_co2)),
      '_blank',
    );
  };

  const clearEmailStorage = () => {
    localStorage.removeItem(`${STORAGE}:clientes`);
    localStorage.removeItem(`${STORAGE}:codigo_cert`);
    localStorage.removeItem(`${STORAGE}:link_cert`);
    localStorage.removeItem(`${STORAGE}:total_t`);
    localStorage.removeItem(`${STORAGE}:nome_projeto`);
  };

  const limparAnexo = (tipo: 'certificadora' | 'validadora') => {
    if (tipo === 'certificadora') {
      setCertificado({
        ...certificado,
        anexo_certificadora_file: {} as File,
        anexo_certificadora_nome: '',
      });
    } else {
      setCertificado({
        ...certificado,
        anexo_validadora_file: {} as File,
        anexo_validadora_nome: '',
      });
    }
  };

  const irParaCadastrarCarta = () => {
    onCloseDialog();
    history.push(ROUTER_PATHS.PORTAL.ADMIN.ZERO_CARBON_LETTERS);
  };

  return (
    <>
      <HStack>
        <Text
          size="sm"
          onClick={() => (possui_carta ? handleOpen() : onOpenDialog())}
          textDecoration="underline"
          cursor="pointer"
        >
          Anexar
        </Text>
        {!possui_carta && (
          <Tooltip
            hasArrow
            label="Não existe carta cadastrada para este projeto."
            bg="black"
            rounded="md"
            placement="top"
            shouldWrapChildren
          >
            <Icon as={BsFillInfoCircleFill} color="red.400" />
          </Tooltip>
        )}
      </HStack>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="xl"
        closeOnOverlayClick={false}
      >
        <DrawerOverlay />
        <DrawerContent sx={{ maxWidth: '70vw', width: '70vw' }}>
          <DrawerCloseButton />
          <DrawerHeader mb={4}>
            <Heading fontSize="xl">Anexar Certificado</Heading>
          </DrawerHeader>

          <DrawerBody>
            <Stack gridGap={3}>
              <Heading fontSize="md">Contratos vinculados</Heading>
              <TableContainer>
                <Table type="secondary">
                  <Thead>
                    <Tr>
                      <Th>N° CONTRATO SAP</Th>
                      <Th>N° DO TERMO</Th>
                      <Th textAlign="end">QTDE DE ITENS</Th>
                      <Th textAlign="end">PRAZO</Th>
                      <Th textAlign="end">CONSUMO INICIAL (CO2E)</Th>
                      <Th textAlign="end">CONSUMO MENSAL (CO2E)</Th>
                      <Th textAlign="end">
                        <HStack justifyContent="end">
                          <Text>CONSUMO TOTAL (CO2E)</Text>
                          <Tooltip
                            hasArrow
                            label="Consumo inicial + (Consumo mensal * Prazo)"
                            bg="black"
                            rounded="md"
                            placement="top"
                            shouldWrapChildren
                          >
                            <Icon
                              as={FaInfoCircle}
                              boxSize={4}
                              color="gray.900"
                            />
                          </Tooltip>
                        </HStack>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {!!certificado.preview.contratos &&
                    certificado.preview.contratos.length ? (
                      certificado.preview.contratos.map(it => (
                        <Tr key={it.nu_contrato}>
                          <Td>{it.nu_contrato}</Td>
                          <Td>{it.nu_termo}</Td>
                          <Td textAlign="end">{it.quantidade_equipamentos}</Td>
                          <Td textAlign="end">{it.prazo_em_meses}</Td>
                          <Td textAlign="end">
                            {it.formattedConsumoInicialCo2Kg}kg
                          </Td>
                          <Td textAlign="end">
                            {it.formattedConsumoMensalCo2Kg}kg
                          </Td>
                          <Td textAlign="end">
                            {it.formattedConsumoTotalCo2Kg}kg
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={7} textAlign="center">
                          {' '}
                          Nenhum contrato.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
              <HStack
                gridGap={5}
                divider={
                  <Center height="60px">
                    <Divider orientation="vertical" />
                  </Center>
                }
              >
                <Stack gridGap={2} w="30%">
                  <Text fontSize="15px" fontWeight="bold">
                    Total consumido:
                  </Text>
                  <HStack alignItems="baseline" gridGap={0}>
                    <Heading>
                      {certificado.preview?.formattedTotalConsumidoCo2Kg ||
                        '- '}
                      kg
                    </Heading>
                  </HStack>
                </Stack>
                <Stack gridGap={2} w="30%">
                  <Text fontSize="15px" fontWeight="bold">
                    Total a ser compensado:
                  </Text>
                  <HStack alignItems="baseline" gridGap={0}>
                    <Heading>
                      {certificado.preview?.formattedTotalCompensadoCo2t ||
                        '- '}
                      t
                    </Heading>
                  </HStack>
                </Stack>
                <Stack gridGap={2} w="30%">
                  <Text fontSize="15px" fontWeight="bold">
                    Saldo:
                  </Text>
                  <HStack alignItems="baseline" gridGap={0}>
                    <Heading>
                      {certificado.preview?.formattedSaldoCo2Kg || '- '}kg
                    </Heading>
                  </HStack>
                </Stack>
              </HStack>
              <Input
                value={certificado.preview.projeto}
                name="projeto"
                type="text"
                label="Projeto"
                isReadOnly
              />
            </Stack>
            <Stack gridGap={3} mt="40px" gridRowGap={3}>
              <Heading fontSize="md">Certificado da Certificadora</Heading>
              <Input
                value={certificado.codigo_certificado}
                name="codigo"
                type="text"
                isRequired
                label="Código do certificado"
                error={errors.codigo_certificado}
                onChange={e => {
                  clearErrors('codigo_certificado');
                  setCertificado({
                    ...certificado,
                    codigo_certificado: e.target.value,
                  });
                }}
                isDisabled={isFetching}
              />
              <FormControl>
                <FormLabel fontSize="14px" mb={2}>
                  Link do certificado da Certificadora{' '}
                  <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <InputGroup size="md">
                  <Input
                    value={certificado.link_certificado}
                    name="link"
                    type="text"
                    isRequired
                    error={errors.link_certificado}
                    onChange={e => {
                      clearErrors('link_certificado');
                      setCertificado({
                        ...certificado,
                        link_certificado: e.target.value,
                      });
                    }}
                    isDisabled={isFetching}
                  />
                </InputGroup>
              </FormControl>
              <FileUploader
                isRequired
                fileName={certificado.anexo_certificadora_nome}
                onChange={file => {
                  clearErrors('anexo_certificadora_nome');
                  setCertificado({
                    ...certificado,
                    anexo_certificadora_nome: file.name,
                    anexo_certificadora_file: file,
                  });
                }}
                isDisabled={isFetching}
                clearFile={() => {
                  limparAnexo('certificadora');
                }}
                label="Anexar certificado da Certificadora"
                showButton={false}
                errors={errors.anexo_certificadora_nome}
                helpText="Arquivo do tipo pdf. O limite total dos arquivos é de 5MB."
              />
            </Stack>
            <Stack gridGap={3} mt="40px" gridRowGap={3}>
              <Heading fontSize="md">Certificado da Validadora</Heading>
              <FileUploader
                isRequired
                fileName={certificado.anexo_validadora_nome}
                onChange={file => {
                  clearErrors('anexo_validadora_nome');
                  setCertificado({
                    ...certificado,
                    anexo_validadora_nome: file.name,
                    anexo_validadora_file: file,
                  });
                }}
                isDisabled={isFetching}
                clearFile={() => {
                  limparAnexo('validadora');
                }}
                label="Anexar certificado"
                showButton={false}
                errors={errors.anexo_validadora_nome}
                helpText="Arquivo do tipo pdf. O limite total dos arquivos é de 5MB."
              />
            </Stack>
            <Stack gridGap={3} mt="40px" gridRowGap={3} pb="80px">
              <Stack>
                <Heading fontSize="md">Enviar os certificados</Heading>
                <Text fontSize="12px">
                  Informe os clientes que devem receber os certificados.
                </Text>
              </Stack>
              <Stack>
                {certificado.clientes.map((it, index) => (
                  <HStack
                    gridGap={3}
                    alignItems="top"
                    key={`${index + 1}-nota`}
                  >
                    <Input
                      name="nome_cliente"
                      type="text"
                      isRequired
                      label="Nome do Cliente"
                      error={errors[`nome_cliente-${index}`]}
                      onChange={e => {
                        clearErrors(`nome_cliente-${index}`);
                        const clientes = certificado.clientes.map((c, i) =>
                          i === index
                            ? { ...c, nome_cliente: e.target.value }
                            : c,
                        );
                        setCertificado({ ...certificado, clientes });
                        if (!e.target.value.trim()) {
                          setError(`nome_cliente-${index}`, {
                            type: 'required',
                            message: 'Campo obrigatório',
                          });
                        }
                      }}
                      isDisabled={isFetching}
                    />
                    <Input
                      name="email_cliente"
                      type="email"
                      isRequired
                      label="E-mail"
                      error={errors[`email_cliente-${index}`]}
                      onChange={e => {
                        clearErrors(`email_cliente-${index}`);
                        const clientes = certificado.clientes.map((c, i) =>
                          i === index
                            ? { ...c, email_cliente: e.target.value }
                            : c,
                        );
                        setCertificado({ ...certificado, clientes });
                        if (!e.target.value.trim()) {
                          setError(`email_cliente-${index}`, {
                            type: 'required',
                            message: 'Campo obrigatório',
                          });
                        }
                        if (!validateNotificationEmail(e.target.value)) {
                          setError(`email_cliente-${index}`, {
                            type: 'email',
                            message: 'E-mail inválido',
                          });
                        }
                      }}
                      isDisabled={isFetching}
                    />
                    {index > 0 && (
                      <IconButton
                        marginTop="30px !important"
                        aria-label="Remover nota fiscal"
                        icon={<Icon as={FiXCircle} />}
                        onClick={() => {
                          const clientes = certificado.clientes.filter(
                            (_, i) => i !== index,
                          );
                          setCertificado({ ...certificado, clientes });
                        }}
                        isDisabled={isFetching}
                      />
                    )}
                    {index === 0 && (
                      <IconButton
                        marginTop="30px !important"
                        aria-label="Adicionar nota fiscal"
                        icon={<Icon as={FiPlusCircle} />}
                        onClick={() => {
                          const clientes = [
                            ...certificado.clientes,
                            { nome_cliente: '', email_cliente: '' },
                          ];
                          setCertificado({ ...certificado, clientes });
                        }}
                        isDisabled={isFetching}
                      />
                    )}
                  </HStack>
                ))}
              </Stack>
            </Stack>
            <Alert
              bg="#f7f2fa"
              borderRadius="md"
              color="#9246ce"
              fontSize="sm"
              py={1}
              px={2}
            >
              <AlertDescription>
                <b>Atenção:</b> Ao clicar em Salvar, enviaremos ao cliente os
                certificados anexados.
              </AlertDescription>
            </Alert>
          </DrawerBody>

          <DrawerFooter w="100%">
            <Flex justifyContent="space-between" w="100%" alignItems="center">
              <Button
                variant="secondary"
                backgroundColor="blackAlpha.900"
                color="white"
                onClick={visualizarEmail}
                ms={2}
              >
                Visualizar e-mail
              </Button>
              <Flex gridGap={2}>
                <Button variant="solid" onClick={() => handleClose()}>
                  Cancelar
                </Button>
                <Button
                  variant="secondary"
                  backgroundColor="blackAlpha.900"
                  color="white"
                  onClick={salvar}
                  ms={2}
                  isDisabled={isFetching}
                >
                  Salvar {isFetching && <Spinner ms={1} />}
                </Button>
              </Flex>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <ConfirmationDialog
        confirmar={() => irParaCadastrarCarta()}
        onClose={onCloseDialog}
        isOpen={isOpenDialog}
        titulo="Alerta!"
        btnConfirmText="Cadastrar Carta"
        btnCancelText="Fechar"
        colorfulBar={false}
        size="lg"
      >
        <Stack gridGap={3}>
          <Text>
            {`Não existe carta cadastrada para este projeto. Clique no botão
              "Cadastrar Carta" ou acesse:`}
          </Text>
          <Text>{`Administração > Zero Carbon > Carta > Cadastrar Carta`}</Text>
        </Stack>
      </ConfirmationDialog>
    </>
  );
};
